import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Bell, CircleUser, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  fetchAdminNotifications,
  markNotificationAsRead,
} from "../../store/reducers/slices/notificationSlice";
import { initialsOf } from "../admin/AdminUi";

const sectionTitles: Record<string, string> = {
  "/admin/dashboard": "Dashboard",
  "/admin/properties": "Properties",
  "/admin/kyc-status": "KYC desk",
  "/admin/users": "Users",
  "/admin/bookings": "Bookings",
  "/admin/deal-room": "Deal rooms",
  "/admin/waiting-list": "Waiting list",
  "/admin/notifications": "Notifications",
  "/admin/settings": "Settings",
};

function sectionTitle(pathname: string) {
  const match = Object.keys(sectionTitles)
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  return match ? sectionTitles[match] : "Admin console";
}

export default function Topbar({ onMenu }: { onMenu?: () => void }) {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const admin = useAppSelector((state) => state.admin.admin);
  const notifications = useAppSelector(
    (state) => state.notification.notifications
  );
  const unreadCount = useAppSelector((state) => state.notification.unreadCount);
  const loading = useAppSelector((state) => state.notification.loading);

  const [openNotification, setOpenNotification] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState<
    string | number | null
  >(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    dispatch(fetchAdminNotifications());

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [dispatch]);

  const latestNotifications = useMemo(
    () =>
      [...notifications]
        .filter((notification) => !notification.is_read)
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        .slice(0, 5),
    [notifications]
  );

  const selectedNotification = notifications.find(
    (notification) => String(notification.id) === String(selectedNotificationId)
  );

  const handleEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenNotification(true);
  }, []);

  const handleLeave = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setOpenNotification(false);
    }, 200);
  }, []);

  const handleSelectNotification = useCallback(
    (notification: (typeof notifications)[number]) => {
      setSelectedNotificationId(notification.id);
      setOpenNotification(false);

      if (!notification.is_read) {
        dispatch(markNotificationAsRead(String(notification.id)));
      }
    },
    [dispatch, notifications]
  );

  const displayName =
    [admin?.first_name, admin?.last_name].filter(Boolean).join(" ") ||
    admin?.name ||
    "Administrator";

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <>
      <motion.div
        initial={{ y: -12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="flex flex-1 items-center justify-between gap-3"
      >
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onMenu}
            aria-label="Open navigation"
            className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-900 hover:text-white md:hidden"
          >
            <Menu size={19} />
          </button>

          <div className="min-w-0">
            <p className="truncate text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
              {sectionTitle(location.pathname)}
            </p>
            <p className="hidden text-[11px] text-zinc-600 sm:block">{today}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-5">
          {/* NOTIFICATIONS */}
          <div
            className="relative"
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
          >
            <button
              type="button"
              aria-label="Open notifications"
              aria-expanded={openNotification}
              onClick={() => setOpenNotification((previous) => !previous)}
              className="relative rounded-lg border border-zinc-800 p-2 text-zinc-400 transition hover:border-zinc-700 hover:text-primary"
            >
              <Bell size={17} />

              {unreadCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {openNotification && (
              <div className="absolute right-0 z-[100] mt-3 w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 text-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-zinc-800 p-4">
                  <div>
                    <h3 className="text-sm font-semibold">Notifications</h3>
                    <p className="mt-1 text-[11px] text-zinc-500">
                      {unreadCount} unread
                    </p>
                  </div>

                  <Bell size={17} className="text-primary" />
                </div>

                {loading && notifications.length === 0 ? (
                  <div className="p-5 text-center text-sm text-zinc-400">
                    Loading notifications...
                  </div>
                ) : latestNotifications.length === 0 ? (
                  <div className="p-6 text-center">
                    <Bell size={24} className="mx-auto mb-2 text-zinc-600" />
                    <p className="text-sm text-zinc-300">
                      No unread notifications
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">
                      New admin alerts will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="max-h-[340px] overflow-y-auto">
                    {latestNotifications.map((notification) => (
                      <button
                        type="button"
                        key={notification.id}
                        onClick={() => handleSelectNotification(notification)}
                        className="w-full border-b border-zinc-800/80 p-3 text-left transition hover:bg-zinc-900"
                      >
                        <div className="flex gap-3">
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />

                          <div className="min-w-0 flex-1">
                            <p className="break-words text-sm font-medium text-zinc-100">
                              {notification.title}
                            </p>

                            <p className="mt-1 line-clamp-2 break-words text-xs text-zinc-400">
                              {notification.message}
                            </p>

                            <p className="mt-2 text-[10px] text-zinc-600">
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <Link
                  to="/admin/notifications"
                  onClick={() => setOpenNotification(false)}
                  className="block p-3 text-center text-xs font-semibold text-primary transition hover:bg-zinc-900"
                >
                  View all notifications
                </Link>
              </div>
            )}
          </div>

          {/* ADMIN IDENTITY */}
          <Link
            to="/admin/settings"
            className="flex items-center gap-3 border-l border-zinc-800 pl-3 sm:pl-5"
          >
            <div className="hidden text-right sm:block">
              <p className="text-[11px] font-bold uppercase tracking-wider text-white">
                {displayName}
              </p>
              <p className="text-[9px] uppercase tracking-wider text-primary">
                Administrator
              </p>
            </div>

            {admin?.image_url ? (
              <img
                src={admin.image_url}
                alt="Admin profile"
                className="h-9 w-9 rounded-full object-cover"
              />
            ) : admin ? (
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-700 bg-zinc-800 text-[10px] font-bold text-zinc-300">
                {initialsOf(admin.first_name, admin.last_name) || "AD"}
              </span>
            ) : (
              <CircleUser size={32} className="text-zinc-400" />
            )}
          </Link>
        </div>
      </motion.div>

      {/* NOTIFICATION DETAILS */}
      {selectedNotification && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm"
          onClick={() => setSelectedNotificationId(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-lg overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 text-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-zinc-800 p-4">
              <div>
                <h2 className="text-base font-semibold">
                  Notification details
                </h2>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-primary">
                  Admin notification
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedNotificationId(null)}
                aria-label="Close notification details"
                className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-900 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[65vh] space-y-4 overflow-y-auto p-5">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Title
                </p>
                <h3 className="mt-1 break-words text-lg font-semibold">
                  {selectedNotification.title}
                </h3>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Message
                </p>
                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-zinc-300">
                  {selectedNotification.message}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Type
                  </p>
                  <p className="mt-1 break-words text-sm">
                    {selectedNotification.type}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Status
                  </p>
                  <p
                    className={`mt-1 text-sm font-medium ${
                      selectedNotification.is_read
                        ? "text-emerald-400"
                        : "text-red-400"
                    }`}
                  >
                    {selectedNotification.is_read ? "Read" : "Unread"}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Date
                </p>
                <p className="mt-1 text-sm text-zinc-300">
                  {new Date(selectedNotification.created_at).toLocaleString()}
                </p>
              </div>

              {selectedNotification.data?.property && (
                <div className="space-y-2 rounded-xl border border-zinc-800 bg-zinc-900 p-4">
                  <p className="text-[10px] uppercase tracking-widest text-primary">
                    Property information
                  </p>
                  <p className="break-words text-sm font-semibold">
                    {selectedNotification.data.property.title}
                  </p>
                  {selectedNotification.data.property.address && (
                    <p className="break-words text-xs text-zinc-400">
                      {selectedNotification.data.property.address}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end border-t border-zinc-800 p-3">
              <button
                type="button"
                onClick={() => setSelectedNotificationId(null)}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
